import { useRef, useState } from "react";
import { Card, Flex, LoadingIndicator } from "@jsq/ds/core";
import { Form, SubmitButton } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { useFeatureFlag } from "@jsq/flags-react";
import { withClientContext } from "~/apollo/client";
import { useGetEntityMixFundStatusLazyQuery } from "~/graphql/mainHooks";
import { useGetEntityGlSyncSettingsQuery } from "~/graphql/supergraphHooks";
import { formatDate } from "~/pages/FundAccounting/utils/helpers";
import { SPACING } from "~/styles/variables";
import { DATE_FORMATS } from "~/utils/constants";
import { toStandardFormat } from "~/utils/datetime";
import { toFederationGlobalId, toGlobalId } from "~/utils/graphql-helpers";
import {
  INITIAL_VALUES,
  SYNC_CUSTOM_DATE_SETTINGS_TYPES,
  SYNC_SETTINGS_TYPES,
} from "./constants";
import CustomDateCard from "./CustomDateCard";
import HasMixedFundsBanner from "./HasMixedFundsBanner";
import { InitialValues } from "./types";

type Props = {
  entityId?: number;
  arenaId?: number;
  arenaName: string;
  entityName: string;
  arenaSubdomain: string;
  onNext: (startDate: string | null, endDate: string, includeAllTransactions: boolean) => void;
  glBatchDates: string[];
  syncFromTransferHistory: boolean;
};

const SyncSettings = ({
  entityId,
  arenaId,
  onNext,
  glBatchDates,
  arenaName,
  entityName,
  arenaSubdomain,
  syncFromTransferHistory,
}: Props) => {
  const hasMixedFundsRef = useRef<boolean>(false);
  const [disableNext, setDisableNext] = useState(false);
  const globalArenaId = toFederationGlobalId("ArenaObjectType", arenaId ? arenaId : "-1");
  const selectedEntityId = entityId ? entityId : -1;
  const [fetchMixFundStatus] = useGetEntityMixFundStatusLazyQuery({
    context: withClientContext("staff", {
      arenaId: arenaId,
    }),
    fetchPolicy: "network-only",
  });

  const { data: entityGlSyncSettingsData, loading } = useGetEntityGlSyncSettingsQuery({
    variables: {
      arenaId: globalArenaId,
      entityId: selectedEntityId,
    },

    fetchPolicy: "network-only",
    skip: !arenaId || !entityId,
  });
  const transferSyncSettings = entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings;
  const enableResyncTransfer = useFeatureFlag("resyncTransfer", false);

  if (loading) {
    return <LoadingIndicator />;
  }

  const { glAsOfDate, glStartDate: glStartDateTransferSyncSettings } =
    transferSyncSettings || {};

  const getEndDate = (values: InitialValues) => {
    let endDate: string = glAsOfDate ?? "";
    if (values.syncSettings === SYNC_SETTINGS_TYPES.allTransferBatches) {
      endDate = glBatchDates[glBatchDates.length - 1];
    }
    if (values.syncSettings === SYNC_SETTINGS_TYPES.untilCustomDate) {
      if (
        values.syncCustomDateSettings === SYNC_CUSTOM_DATE_SETTINGS_TYPES.untilLastSyncedGlDate
      ) {
        endDate = glAsOfDate ?? "";
      } else if (
        values.syncCustomDateSettings === SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate &&
        values.customGlDate
      ) {
        endDate = values.customGlDate?.toString() || "";
      }
      endDate = endDate
        ? toStandardFormat(new Date(endDate), DATE_FORMATS["YYYY-MM-DD"].long)
        : "";
    }
    return endDate;
  };

  const getEndDateNew = (values: InitialValues) => {
    let endDate: string = glAsOfDate ?? "";
    if (values.syncSettings === SYNC_SETTINGS_TYPES.allTransferBatches) {
      endDate = glBatchDates[glBatchDates.length - 1];
    }
    if (values.syncSettings === SYNC_SETTINGS_TYPES.untilCustomDate) {
      endDate = values.customGlDate
        ? toStandardFormat(new Date(values.customGlDate), DATE_FORMATS["YYYY-MM-DD"].long)
        : "";
    }
    return endDate;
  };

  const getStartDate = (values: InitialValues) => {
    if (values.useInceptionDate) {
      return glStartDateTransferSyncSettings;
    }

    return values.glStartDate === ""
      ? null
      : toStandardFormat(new Date(values.glStartDate), DATE_FORMATS["YYYY-MM-DD"].long);
  };

  const checkHasMixedFunds = async (values: InitialValues) => {
    const endDate = enableResyncTransfer ? getEndDateNew(values) : getEndDate(values);
    if (!endDate) {
      return false;
    }
    const { data: mixFundStatusData } = await fetchMixFundStatus({
      variables: {
        entityId: toGlobalId("AccountObjectType", entityId || -1),
      },
    });
    hasMixedFundsRef.current =
      !!mixFundStatusData?.investmentEntity?.hasMixedCallsAndStandaloneContributions;
    return hasMixedFundsRef.current;
  };
  const handleSubmit = (values: InitialValues) => {
    const endDate = enableResyncTransfer ? getEndDateNew(values) : getEndDate(values);
    const startDate = enableResyncTransfer
      ? getStartDate(values)
      : glStartDateTransferSyncSettings;
    onNext(
      startDate ?? null,
      endDate,
      values.syncSettings === SYNC_SETTINGS_TYPES.untilCustomDate,
    );
  };

  const handleValidate = async (values: InitialValues) => {
    const hasMixedFunds = await checkHasMixedFunds(values);
    if (hasMixedFunds) {
      setDisableNext(true);
      return undefined;
    }
    if (
      values.syncSettings === SYNC_SETTINGS_TYPES.untilCustomDate &&
      values.syncCustomDateSettings === SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate &&
      !values.customGlDate
    ) {
      setDisableNext(true);
      return {
        [SYNC_CUSTOM_DATE_SETTINGS_TYPES.customGlDate]: "Please input a date",
      };
    }

    setDisableNext(false);
    return undefined;
  };

  return (
    <Flex flexDirection="column" alignItems="center">
      {hasMixedFundsRef.current && disableNext && (
        <div style={{ width: "800px" }}>
          <HasMixedFundsBanner
            arenaName={arenaName}
            entityName={entityName}
            arenaSubdomain={arenaSubdomain}
            entityId={entityId || -1}
            hasMixedFunds={hasMixedFundsRef.current}
          />
        </div>
      )}
      <Flex flexDirection="row" style={{ marginBottom: SPACING.XXX_LARGE, width: "750px" }}>
        <ColumnContainer>
          <Column size="md">
            <Flex flexDirection="column">
              <Form
                initialValues={
                  syncFromTransferHistory
                    ? {
                        ...INITIAL_VALUES,
                        syncSettings: SYNC_SETTINGS_TYPES.allTransferBatches,
                      }
                    : INITIAL_VALUES
                }
                onSubmit={handleSubmit}
                validate={handleValidate}
                validateOnMount
                submitOnlyChangedValues={false}
              >
                <Card
                  className="inset-lg"
                  title="Sync settings"
                  style={{ marginTop: SPACING.LARGE }}
                >
                  <CustomDateCard
                    glStartDate={glStartDateTransferSyncSettings || undefined}
                    syncFromTransferHistory={syncFromTransferHistory}
                    batchDates={glBatchDates}
                    glAsOfDate={formatDate(glAsOfDate ?? null)}
                  />
                </Card>

                <Flex style={{ marginTop: SPACING.LARGE }} justifyContent="flex-end">
                  <SubmitButton disabled={disableNext}>Next</SubmitButton>
                </Flex>
              </Form>
            </Flex>
          </Column>
        </ColumnContainer>
      </Flex>
    </Flex>
  );
};

export default SyncSettings;
